import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPinned, UserPlus, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Field } from "../components/Field";
import { CompanionOverlay } from "../components/CompanionOverlay";
import { SessionFields, SessionFormValue } from "../components/SessionFields";
import { AnglerRow } from "../components/AnglerTag";
import { SessionRodSetups } from "../components/SessionRodSetups";
import { SwimObservations } from "../components/SwimObservations";
import { LiveSaveSpot } from "../components/LiveSaveSpot";
import { OfflineSessionDetailsPage } from "./OfflineSessionDetails";
import {
  formatDateTime,
  toDateTimeInput,
  totalWeightLabel,
  weightLabel,
} from "../lib/format";

const SESSION_REPORT_MARKER = "--- CatchCompass session report ---";

function splitSessionNotes(notes: unknown) {
  const full = String(notes ?? "");
  const markerIndex = full.indexOf(SESSION_REPORT_MARKER);
  if (markerIndex < 0) return { manualNotes: full.trim(), report: "" };
  return {
    manualNotes: full.slice(0, markerIndex).trim(),
    report: full.slice(markerIndex + SESSION_REPORT_MARKER.length).trim(),
  };
}

function preserveSessionReport(manualNotes: string, existingNotes: unknown) {
  const { report } = splitSessionNotes(existingNotes);
  const cleanManual = manualNotes.trim();
  if (!report) return cleanManual;
  return cleanManual
    ? `${cleanManual}\n\n${SESSION_REPORT_MARKER}\n${report}`
    : `${SESSION_REPORT_MARKER}\n${report}`;
}

function hasRecordedCatchWeight(row: any) {
  const pounds = Number(row?.weight_lbs ?? row?.weight_lb ?? 0);
  const ounces = Number(row?.weight_oz ?? 0);
  return pounds > 0 || ounces > 0;
}

function catchClockTime(value: string | null | undefined) {
  if (!value) return "Time not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

export default function SessionDetailsPage() {
  const { id } = useParams();
  if (id?.startsWith("offline-")) {
    return <OfflineSessionDetailsPage id={id} />;
  }
  return <OnlineSessionDetailsPage />;
}

function OnlineSessionDetailsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () =>
      fetch(`/app-api/sessions/${id}`).then((r) => {
        if (!r.ok) throw new Error("Session not found");
        return r.json();
      }),
  });
  const lakesQuery = useQuery({
    queryKey: ["carpintel_lakes"],
    queryFn: () => fetch("/app-api/lakes").then((r) => r.json()),
  });
  const catchesQuery = useQuery({
    queryKey: ["carpintel_catches"],
    queryFn: () => fetch("/app-api/catches").then((r) => r.json()),
  });
  const catches = (catchesQuery.data ?? []).filter(
    (catchRow: any) => String(catchRow.session_id) === String(id),
  );
  const weighedCatches = catches.filter(hasRecordedCatchWeight);
  const missingWeightCatches = catches.filter(
    (catchRow: any) => !hasRecordedCatchWeight(catchRow),
  );
  const pegIntervalsQuery = useQuery({
    queryKey: ["carpintel_session_peg_intervals", id],
    queryFn: () =>
      fetch(`/app-api/sessions/${id}/peg-intervals`).then((r) =>
        r.ok ? r.json() : [],
      ),
    enabled: Boolean(id),
  });
  const [values, setValues] = useState<SessionFormValue>({
    lake_id: "",
    peg: "",
    start_at: "",
    end_at: "",
    rods_count: "3",
    target_species: "both",
    weather: "",
    rain_observed: "not-recorded",
    pressure_trend: "not-recorded",
    air_temp_c: "",
    wind_direction: "",
    wind_speed_mph: "",
    air_pressure_hpa: "",
    cloud_cover_pct: "",
    water_temp_c: "",
    gps_latitude: "",
    gps_longitude: "",
    privacy_setting: "Contribute anonymously to CarpIntel statistics",
    share_peg_anonymously: false,
    notes: "",
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [refreshingAdvice, setRefreshingAdvice] = useState(false);
  const [refreshAdviceError, setRefreshAdviceError] = useState("");

  // Deletion state
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // End-session state
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState("");
  const [endReport, setEndReport] = useState("");
  const [reviewingMissingWeights, setReviewingMissingWeights] = useState(false);
  const [missingWeightDrafts, setMissingWeightDrafts] = useState<
    Record<string, { lb: string; oz: string }>
  >({});
  const [savingMissingWeights, setSavingMissingWeights] = useState(false);
  const [missingWeightError, setMissingWeightError] = useState("");
  const [weatherSignal, setWeatherSignal] = useState<any | null>(null);
  const [companionQuestion, setCompanionQuestion] = useState<{
    id: string;
    text: string;
  } | null>(null);

  useEffect(() => {
    if (!id || !sessionQuery.data || sessionQuery.data.end_at) return;

    let stopped = false;
    const syncWeatherTimeline = async () => {
      if (stopped || !navigator.onLine) return;
      try {
        const response = await fetch(`/app-api/sessions/${id}/weather-sync`, {
          method: "POST",
        });
        if (!response.ok) return;
        const result = await response.json().catch(() => ({}));
        const signal = result?.meaningful_change;
        if (!signal?.id || stopped) return;
        const seenKey = `catchcompass:weather-signal-seen:${id}`;
        if (window.localStorage.getItem(seenKey) !== String(signal.id)) {
          setWeatherSignal(signal);
        }
      } catch {
        // Weather evidence is best-effort and must never interrupt bank-side use.
      }
    };

    void syncWeatherTimeline();
    const timer = window.setInterval(
      () => void syncWeatherTimeline(),
      60 * 60 * 1000,
    );

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [id, sessionQuery.data?.end_at]);

  async function handleEndSession() {
    setEnding(true);
    setEndError("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/end`, {
        method: "POST",
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not end session");
      setEndReport(result.session_report || "");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
      ]);
      setShowEndConfirm(false);
    } catch (err) {
      setEndError(err instanceof Error ? err.message : "Could not end session");
    } finally {
      setEnding(false);
    }
  }

  function beginMissingWeightReview() {
    const drafts: Record<string, { lb: string; oz: string }> = {};
    for (const catchRow of missingWeightCatches) {
      drafts[String(catchRow.id)] = { lb: "", oz: "" };
    }
    setMissingWeightDrafts(drafts);
    setMissingWeightError("");
    setReviewingMissingWeights(true);
  }

  async function saveMissingWeightsAndEnd() {
    setMissingWeightError("");
    const missingEntries = missingWeightCatches.map((catchRow: any) => {
      const draft = missingWeightDrafts[String(catchRow.id)] ?? {
        lb: "",
        oz: "",
      };
      return { catchRow, draft };
    });

    const incomplete = missingEntries.find(({ draft }) => {
      const lbText = draft.lb.trim();
      const ozText = draft.oz.trim();
      if (!lbText && !ozText) return true;
      const pounds = Number(lbText || 0);
      const ounces = Number(ozText || 0);
      return (
        !Number.isFinite(pounds) ||
        !Number.isFinite(ounces) ||
        pounds < 0 ||
        pounds > 200 ||
        ounces < 0 ||
        ounces > 15 ||
        (pounds === 0 && ounces === 0)
      );
    });

    if (incomplete) {
      setMissingWeightError(
        "Add a valid weight for every fish, or go back and choose End session without weights.",
      );
      return;
    }

    setSavingMissingWeights(true);
    try {
      for (const { catchRow, draft } of missingEntries) {
        const response = await fetch(
          `/app-api/catches/${catchRow.id}/weight`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              weight_lb: Number(draft.lb || 0),
              weight_oz: Number(draft.oz || 0),
            }),
          },
        );
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Could not save catch weight");
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] });
      await queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] });
      setReviewingMissingWeights(false);
      await handleEndSession();
    } catch (err) {
      setMissingWeightError(
        err instanceof Error ? err.message : "Could not save catch weights",
      );
    } finally {
      setSavingMissingWeights(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await fetch(`/app-api/sessions/${id}`, {
        method: "DELETE",
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not delete session");
      // Invalidate all relevant queries so nothing stale remains
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
        queryClient.invalidateQueries({
          queryKey: ["carpintel_active_session"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["carpintel_advisor_recommendations"],
        }),
      ]);
      navigate("/app", { replace: true, state: { deleted: result.message } });
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Could not delete session",
      );
      setDeleting(false);
    }
  }
  const session = sessionQuery.data;
  const pegIntervals: any[] = pegIntervalsQuery.data ?? [];
  const currentPegInterval = pegIntervals.find((interval: any) => !interval.ended_at);

  // Anglers
  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", id],
    queryFn: () =>
      fetch(`/app-api/sessions/${id}/anglers`).then((r) => r.json()),
    enabled: Boolean(id),
  });
  const anglers: any[] = anglersQuery.data ?? [];
  const ownerAngler = anglers.find((angler: any) => angler.is_owner) ?? null;

  // Add-angler form
  const [showAddAngler, setShowAddAngler] = useState(false);
  const [anglerForm, setAnglerForm] = useState({
    display_name: "",
    peg_or_swim: "",
    rods_count: "3",
  });
  const [anglerSaving, setAnglerSaving] = useState(false);
  const [anglerMessage, setAnglerMessage] = useState("");

  // Edit-angler state
  const [editingAnglerId, setEditingAnglerId] = useState<number | null>(null);
  const [editAnglerForm, setEditAnglerForm] = useState({
    display_name: "",
    peg_or_swim: "",
    rods_count: "3",
  });
  const [editAnglerSaving, setEditAnglerSaving] = useState(false);
  const [editAnglerMessage, setEditAnglerMessage] = useState("");

  async function addAngler(event: React.FormEvent) {
    event.preventDefault();
    setAnglerMessage("");
    if (!anglerForm.display_name.trim()) {
      setAnglerMessage("Enter the angler's name.");
      return;
    }
    setAnglerSaving(true);
    try {
      const response = await fetch(`/app-api/sessions/${id}/anglers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name: anglerForm.display_name.trim(),
          peg_or_swim: anglerForm.peg_or_swim.trim() || null,
          rods_count: Number(anglerForm.rods_count || 1),
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not add angler");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["carpintel_session_anglers", id],
        }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
      ]);
      setAnglerForm({ display_name: "", peg_or_swim: "", rods_count: "3" });
      setShowAddAngler(false);
    } catch (caught) {
      setAnglerMessage(
        caught instanceof Error ? caught.message : "Could not add angler",
      );
    }
    setAnglerSaving(false);
  }

  function startEditAngler(angler: any) {
    setEditingAnglerId(Number(angler.id));
    setEditAnglerForm({
      display_name: angler.display_name,
      peg_or_swim: angler.peg_or_swim || "",
      rods_count: String(angler.rods_count || 1),
    });
    setEditAnglerMessage("");
  }

  async function saveEditAngler(event: React.FormEvent, anglerId: number) {
    event.preventDefault();
    setEditAnglerMessage("");
    if (!editAnglerForm.display_name.trim()) {
      setEditAnglerMessage("Enter the angler's name.");
      return;
    }
    setEditAnglerSaving(true);
    try {
      const response = await fetch(
        `/app-api/sessions/${id}/anglers/${anglerId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            display_name: editAnglerForm.display_name.trim(),
            peg_or_swim: editAnglerForm.peg_or_swim.trim() || null,
            rods_count: Number(editAnglerForm.rods_count || 1),
          }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Could not save changes");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["carpintel_session_anglers", id],
        }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
      ]);
      setEditingAnglerId(null);
    } catch (caught) {
      setEditAnglerMessage(
        caught instanceof Error ? caught.message : "Could not save changes",
      );
    }
    setEditAnglerSaving(false);
  }

  async function removeAngler(anglerId: number) {
    const response = await fetch(
      `/app-api/sessions/${id}/anglers/${anglerId}`,
      {
        method: "DELETE",
      },
    );
    const result = await response.json();
    if (!response.ok) {
      alert(result.error || "Could not remove angler");
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["carpintel_session_anglers", id],
      }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
    ]);
  }

  useEffect(() => {
    if (session)
      setValues({
        lake_id: String(session.lake_id),
        peg: session.peg || "",
        start_at: toDateTimeInput(session.start_at),
        end_at: toDateTimeInput(session.end_at),
        rods_count: String(ownerAngler?.rods_count || session.rods_count || 1),
        target_species: session.target_species || "both",
        weather: session.weather || "",
        rain_observed:
          session.rain_observed === true
            ? "true"
            : session.rain_observed === false
              ? "false"
              : "not-recorded",
        pressure_trend: session.pressure_trend || "not-recorded",
        air_temp_c: session.air_temp_c ?? "",
        wind_direction: session.wind_direction || "",
        wind_speed_mph: session.wind_speed_mph ?? "",
        air_pressure_hpa: session.air_pressure_hpa ?? "",
        cloud_cover_pct: session.cloud_cover_pct ?? "",
        water_temp_c: session.water_temp_c ?? "",
        gps_latitude: session.gps_latitude ?? "",
        gps_longitude: session.gps_longitude ?? "",
        privacy_setting:
          session.privacy_setting ||
          "Contribute anonymously to CarpIntel statistics",
        share_peg_anonymously: Boolean(session.share_peg_anonymously),
        notes: splitSessionNotes(session.notes).manualNotes,
      });
  }, [session, ownerAngler?.id, ownerAngler?.rods_count]);

  async function handleRefreshTodayAdvice() {
    if (!session || session.end_at || !values.lake_id) return;
    setRefreshingAdvice(true);
    setRefreshAdviceError("");
    try {
      const now = new Date();
      const pad = (value: number) => String(value).padStart(2, "0");
      const fishingDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const startTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

      let weather: Record<string, any> = {};
      try {
        const weatherResponse = await fetch(
          `/app-api/advisor/current-weather?lake_id=${encodeURIComponent(values.lake_id)}`,
        );
        if (weatherResponse.ok) {
          const weatherData = await weatherResponse.json();
          if (weatherData.ok) weather = weatherData;
        }
      } catch {
        // Advice can still be generated when the weather provider is unavailable.
      }

      const createAdvice = async (species: "carp" | "catfish") => {
        const adviceResponse = await fetch("/app-api/advisor/recommendations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            is_future_trip: false,
            lake_id: Number(values.lake_id),
            fishing_date: fishingDate,
            expected_start_time: startTime,
            expected_duration_hours: 24,
            target_species: species,
            forecast_pressure_trend: weather.pressure_trend || "",
            forecast_air_temp_c: weather.air_temp_c ?? null,
            forecast_pressure_hpa: weather.pressure_hpa ?? null,
            forecast_wind_direction: weather.wind_direction ?? null,
            forecast_wind_speed_mph: weather.wind_speed_mph ?? null,
            forecast_cloud_pct: weather.cloud_pct ?? null,
            forecast_precipitation_mm: weather.precipitation_mm ?? null,
          }),
        });
        const advice = await adviceResponse.json();
        if (!adviceResponse.ok || !advice.id) {
          throw new Error(advice.error || `Could not refresh ${species} advice`);
        }
        return advice;
      };

      const linkAdvice = async (adviceId: number | string) => {
        const linkResponse = await fetch(
          `/app-api/advisor/recommendations/${adviceId}/link`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ actual_session_id: Number(id) }),
          },
        );
        const link = await linkResponse.json();
        if (!linkResponse.ok) {
          throw new Error(link.error || "Could not link advice to this session");
        }
      };

      if (values.target_species === "both") {
        const [carpAdvice, catfishAdvice] = await Promise.all([
          createAdvice("carp"),
          createAdvice("catfish"),
        ]);
        await linkAdvice(catfishAdvice.id);
        await linkAdvice(carpAdvice.id);
        queryClient.invalidateQueries({
          queryKey: ["carpintel_advisor_recommendations"],
        });
        navigate(
          `/advisor/both?carp=${encodeURIComponent(String(carpAdvice.id))}&catfish=${encodeURIComponent(String(catfishAdvice.id))}`,
        );
      } else {
        const species = values.target_species === "catfish" ? "catfish" : "carp";
        const advice = await createAdvice(species);
        await linkAdvice(advice.id);
        queryClient.invalidateQueries({
          queryKey: ["carpintel_advisor_recommendations"],
        });
        navigate(`/advisor/${advice.id}`);
      }
    } catch (err) {
      setRefreshAdviceError(
        err instanceof Error ? err.message : "Could not refresh today's advice",
      );
    } finally {
      setRefreshingAdvice(false);
    }
  }

  async function saveSession(event: React.FormEvent) {
    event.preventDefault();
    setMessage("");
    if (!values.lake_id || !values.start_at) {
      setMessage("Choose a lake and start time.");
      return;
    }
    setSaving(true);
    const payload = {
      ...values,
      lake_id: Number(values.lake_id),
      start_at: new Date(values.start_at).toISOString(),
      end_at: values.end_at ? new Date(values.end_at).toISOString() : null,
      rods_count: Number(values.rods_count || 1),
      air_temp_c: values.air_temp_c || null,
      wind_speed_mph: values.wind_speed_mph || null,
      air_pressure_hpa: values.air_pressure_hpa || null,
      cloud_cover_pct: values.cloud_cover_pct || null,
      water_temp_c: values.water_temp_c || null,
      rain_observed: values.rain_observed,
      pressure_trend: values.pressure_trend,
      notes: session?.end_at
        ? preserveSessionReport(values.notes, session.notes)
        : values.notes,
    };
    const response = await fetch(`/app-api/sessions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    setSaving(false);
    if (!response.ok) {
      setMessage(result.error || "Could not save changes");
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: ["carpintel_session", id],
    });
    await queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] });
    await queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] });
    await queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] });
    setEditing(false);
    setMessage("Changes saved");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closeEdit() {
    setEditing(false);
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (sessionQuery.isLoading || lakesQuery.isLoading || catchesQuery.isLoading)
    return <PageMessage text="Loading session…" />;
  if (sessionQuery.error || !session)
    return (
      <PageMessage text="That session could not be found." link="/sessions" />
    );
  const sessionNoteParts = splitSessionNotes(session.notes);
  const displayedSessionReport =
    endReport ||
    (sessionNoteParts.report
      ? `${SESSION_REPORT_MARKER}\n${sessionNoteParts.report}`
      : "");
  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to="/sessions"
          className="text-sm font-medium text-accent hover:underline"
        >
          ← Sessions
        </Link>
        <div className="mt-2 flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 w-full sm:flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold leading-tight tracking-tight text-primary">
                {session.lake_name}
              </h1>
              {!session.end_at ? (
                <span className="inline-flex items-center rounded-full bg-success-weak px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-success">
                  Live
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-secondary">
              {session.peg ? `${session.peg} · ` : ""}
              {formatDateTime(session.start_at)}
              {session.end_at ? ` – ${formatDateTime(session.end_at)}` : ""}
            </p>
            {!session.end_at && currentPegInterval ? (
              <p className="mt-1 text-xs text-tertiary">
                Current peg since {formatDateTime(currentPegInterval.started_at)}
                {pegIntervals.length > 1
                  ? ` · ${pegIntervals.length - 1} peg move${pegIntervals.length - 1 === 1 ? "" : "s"} logged`
                  : ""}
              </p>
            ) : null}
          </div>
          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:shrink-0 sm:flex-wrap sm:justify-end">
            {!session.end_at ? (
              <>
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  onClick={() =>
                    navigate(
                      `/scout?session_id=${encodeURIComponent(String(session.id))}&lake_id=${encodeURIComponent(String(session.lake_id))}&species=${encodeURIComponent(String(values.target_species || "both"))}&mode=move`,
                    )
                  }
                >
                  <MapPinned /> Move peg
                </Button>
                <Button
                  variant="secondary"
                  className="w-full sm:w-auto"
                  onClick={handleRefreshTodayAdvice}
                  isLoading={refreshingAdvice}
                >
                  Refresh today's advice
                </Button>
              </>
            ) : null}
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => (editing ? closeEdit() : setEditing(true))}
            >
              {editing ? "← Back to session" : "Edit session"}
            </Button>
          </div>
        </div>
        {refreshAdviceError ? (
          <p
            className="mt-3 rounded-md bg-error-weak p-3 text-sm text-error"
            role="alert"
          >
            {refreshAdviceError}
          </p>
        ) : null}
      </header>
      {!editing && message === "Changes saved" ? (
        <p className="rounded-md bg-success-weak p-3 text-sm text-success" role="status">
          Changes saved. You are back in the session.
        </p>
      ) : null}
      {editing ? (
        <form onSubmit={saveSession} className="space-y-6">
          <SessionFields
            values={values}
            setValues={setValues}
            lakes={lakesQuery.data ?? []}
            rodsEditable={!session.end_at}
            sessionRodsCount={session?.rods_count}
            rodsLabel="Your rods"
            rodsHelp={
              session.end_at
                ? undefined
                : "Change this if you selected the wrong number before starting. The live rod controls update when you save."
            }
          />
          {message ? (
            <p
              className="rounded-md bg-error-weak p-3 text-sm text-error"
              role="alert"
            >
              {message}
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button type="submit" isLoading={saving}>
              Save changes
            </Button>
            <Button
              type="button"
              variant="tertiary"
              onClick={closeEdit}
            >
              Back to session
            </Button>
          </div>
        </form>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="Catches" value={session.catch_count} />
            <Metric
              label={
                catches.length > 0 && weighedCatches.length < catches.length
                  ? "Recorded weight"
                  : "Total weight"
              }
              value={
                catches.length > 0 && weighedCatches.length === 0
                  ? "Weight not added"
                  : totalWeightLabel(session.total_weight_lbs)
              }
            />
            <Metric label="Rods" value={session.rods_count} />
            <Metric
              label="Water"
              value={
                session.water_temp_c != null ? `${session.water_temp_c}°C` : "–"
              }
            />
          </section>
          <section className="rounded-lg border border-border bg-raised p-4">
            <h2 className="text-lg font-semibold text-primary">Conditions</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
              <MetricText label="Weather" value={session.weather} />
              <MetricText
                label="Rain"
                value={
                  session.rain_observed === true
                    ? "Rain or drizzle"
                    : session.rain_observed === false
                      ? "Dry"
                      : null
                }
              />
              <MetricText
                label="Pressure trend"
                value={session.pressure_trend}
              />
              <MetricText
                label="Air"
                value={
                  session.air_temp_c != null ? `${session.air_temp_c}°C` : null
                }
              />
              <MetricText
                label="Wind"
                value={
                  session.wind_direction
                    ? `${session.wind_direction} ${session.wind_speed_mph || ""}mph`
                    : null
                }
              />
              <MetricText
                label="Pressure"
                value={
                  session.air_pressure_hpa != null
                    ? `${session.air_pressure_hpa} hPa`
                    : null
                }
              />
              <MetricText
                label="Cloud"
                value={
                  session.cloud_cover_pct != null
                    ? `${session.cloud_cover_pct}%`
                    : null
                }
              />
              <MetricText label="Privacy" value={session.privacy_setting} />
              <MetricText
                label="GPS"
                value={
                  session.gps_latitude != null && session.gps_longitude != null
                    ? "Saved privately"
                    : null
                }
              />
              {session.moon_phase ? (
                <MetricText
                  label="Moon"
                  value={`${session.moon_phase}${session.moon_illumination_pct != null ? ` · ${session.moon_illumination_pct}%` : ""}`}
                />
              ) : null}
              {session.moonrise_at ? (
                <MetricText
                  label="Moonrise"
                  value={new Date(session.moonrise_at).toLocaleTimeString(
                    "en-GB",
                    { hour: "2-digit", minute: "2-digit" },
                  )}
                />
              ) : null}
              {session.moonset_at ? (
                <MetricText
                  label="Moonset"
                  value={new Date(session.moonset_at).toLocaleTimeString(
                    "en-GB",
                    { hour: "2-digit", minute: "2-digit" },
                  )}
                />
              ) : null}
            </div>
          </section>
          {weatherSignal ? (
            <section className="rounded-lg border border-accent/40 bg-accent/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                Conditions changed
              </p>
              <p className="mt-2 text-sm font-semibold text-primary">
                {weatherSignal.summary}
              </p>
              <p className="mt-1 text-sm leading-6 text-secondary">
                CatchCompass noticed this automatically. You only need to dig into it if you want to know whether it should change what you are doing.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    window.localStorage.setItem(
                      `catchcompass:weather-signal-seen:${id}`,
                      String(weatherSignal.id),
                    );
                    setCompanionQuestion({
                      id: `weather-${weatherSignal.id}-${Date.now()}`,
                      text: `The latest automatic weather check says: ${weatherSignal.summary}. Does that actually matter to what I should do in this session right now? Keep it practical and tell me if you'd change anything or sit on my hands.`,
                    });
                    setWeatherSignal(null);
                  }}
                >
                  Ask CatchCompass
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    window.localStorage.setItem(
                      `catchcompass:weather-signal-seen:${id}`,
                      String(weatherSignal.id),
                    );
                    setWeatherSignal(null);
                  }}
                >
                  Not now
                </Button>
              </div>
            </section>
          ) : null}
          {sessionNoteParts.manualNotes ? (
            <section className="rounded-lg border border-border bg-raised p-4">
              <h2 className="text-sm font-semibold text-primary">Session notes</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-secondary">
                {sessionNoteParts.manualNotes}
              </p>
            </section>
          ) : null}
          {displayedSessionReport ? (
            <section className="rounded-lg border border-border bg-raised p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-primary">
                  Session report
                </h2>
                <span className="rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
                  Saved to notes
                </span>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-secondary">
                {displayedSessionReport.replace(
                  `${SESSION_REPORT_MARKER}\n`,
                  "",
                )}
              </p>
            </section>
          ) : null}
          {session.end_at ? (
            <section className="rounded-lg border border-accent/40 bg-accent/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                Match of the Day
              </p>
              <p className="mt-1 text-sm leading-6 text-secondary">
                Replay the session chronologically and see the turning points, peg exposure, conditions and what CatchCompass learned.
              </p>
              <Button asChild className="mt-3">
                <Link to={`/sessions/${session.id}/replay`}>Replay this session</Link>
              </Button>
            </section>
          ) : null}
          {!session.end_at ? (
            <div className="space-y-4">
              <SessionRodSetups
                sessionId={session.id}
                anglers={anglers}
                targetSpecies={session.target_species}
                lakeId={session.lake_id}
                sessionStartAt={session.start_at}
                catches={catches}
              />
              <SwimObservations sessionId={session.id} />
              <LiveSaveSpot lakeId={session.lake_id} />
            </div>
          ) : null}
          {/* Anglers section — always shown, compact when solo */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-primary">Anglers</h2>
              {!session.end_at && !showAddAngler ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAddAngler(true)}
                >
                  <UserPlus className="size-4" /> Add angler
                </Button>
              ) : null}
            </div>

            {anglers.map((angler: any) => (
              <div
                key={angler.id}
                className="rounded-lg border border-border bg-raised p-3"
              >
                {editingAnglerId === Number(angler.id) ? (
                  <form
                    onSubmit={(e) => saveEditAngler(e, Number(angler.id))}
                    className="space-y-3"
                  >
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Field label="Name" id={`edit-name-${angler.id}`}>
                        <Input
                          id={`edit-name-${angler.id}`}
                          value={editAnglerForm.display_name}
                          onChange={(e) =>
                            setEditAnglerForm((f) => ({
                              ...f,
                              display_name: e.target.value,
                            }))
                          }
                          required
                        />
                      </Field>
                      <Field label="Peg / swim" id={`edit-peg-${angler.id}`}>
                        <Input
                          id={`edit-peg-${angler.id}`}
                          value={editAnglerForm.peg_or_swim}
                          onChange={(e) =>
                            setEditAnglerForm((f) => ({
                              ...f,
                              peg_or_swim: e.target.value,
                            }))
                          }
                          placeholder="e.g. Peg 8"
                        />
                      </Field>
                      <Field label="Rods" id={`edit-rods-${angler.id}`}>
                        <Input
                          id={`edit-rods-${angler.id}`}
                          type="number"
                          min="1"
                          max="20"
                          value={editAnglerForm.rods_count}
                          onChange={(e) =>
                            setEditAnglerForm((f) => ({
                              ...f,
                              rods_count: e.target.value,
                            }))
                          }
                        />
                      </Field>
                    </div>
                    {editAnglerMessage ? (
                      <p className="text-sm text-error">{editAnglerMessage}</p>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        isLoading={editAnglerSaving}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={() => setEditingAnglerId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <AnglerRow
                      displayName={angler.display_name}
                      isGuest={angler.is_guest}
                      pegOrSwim={angler.peg_or_swim}
                      rodsCount={Number(angler.rods_count)}
                      catchCount={Number(angler.catch_count)}
                    />
                    {!session.end_at || angler.is_guest ? (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => startEditAngler(angler)}
                        >
                          Edit
                        </Button>
                        {angler.is_guest ? (
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() => removeAngler(Number(angler.id))}
                            aria-label={`Remove ${angler.display_name}`}
                          >
                            <X className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}

            {/* Add angler form — only on active session */}
            {showAddAngler && !session.end_at ? (
              <form
                onSubmit={addAngler}
                className="space-y-3 rounded-lg border border-border bg-raised p-4"
              >
                <p className="text-sm font-semibold text-primary">
                  Add guest angler
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Name" id="new-angler-name">
                    <Input
                      id="new-angler-name"
                      value={anglerForm.display_name}
                      onChange={(e) =>
                        setAnglerForm((f) => ({
                          ...f,
                          display_name: e.target.value,
                        }))
                      }
                      placeholder="e.g. Dan"
                      required
                    />
                  </Field>
                  <Field label="Peg / swim" id="new-angler-peg">
                    <Input
                      id="new-angler-peg"
                      value={anglerForm.peg_or_swim}
                      onChange={(e) =>
                        setAnglerForm((f) => ({
                          ...f,
                          peg_or_swim: e.target.value,
                        }))
                      }
                      placeholder="e.g. Peg 8"
                    />
                  </Field>
                  <Field label="Rods" id="new-angler-rods">
                    <Input
                      id="new-angler-rods"
                      type="number"
                      min="1"
                      max="20"
                      value={anglerForm.rods_count}
                      onChange={(e) =>
                        setAnglerForm((f) => ({
                          ...f,
                          rods_count: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>
                {anglerMessage ? (
                  <p className="text-sm text-error">{anglerMessage}</p>
                ) : null}
                <div className="flex gap-2">
                  <Button type="submit" size="sm" isLoading={anglerSaving}>
                    Add angler
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      setShowAddAngler(false);
                      setAnglerMessage("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
          </section>

          {/* ── End Session ── only shown while session is live */}
          {!session.end_at ? (
            <div className="rounded-lg border border-border bg-raised p-4">
              {!showEndConfirm ? (
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-secondary">
                    Ready to wrap up? End the session to lock in your results.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowEndConfirm(true);
                      setEndError("");
                    }}
                  >
                    End session
                  </Button>
                </div>
              ) : reviewingMissingWeights ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-primary">
                      Add missing weights
                    </p>
                    <p className="mt-1 text-sm leading-6 text-secondary">
                      Add the weights you missed, then CatchCompass will save them
                      and end the session.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {missingWeightCatches.map((catchRow: any) => {
                      const key = String(catchRow.id);
                      const draft = missingWeightDrafts[key] ?? {
                        lb: "",
                        oz: "",
                      };
                      return (
                        <div
                          key={catchRow.id}
                          className="rounded-lg border border-border bg-background p-3"
                        >
                          <p className="text-sm font-semibold text-primary">
                            {catchClockTime(catchRow.caught_at)} ·{" "}
                            {catchRow.species || catchRow.catch_species || "Fish"}
                            {catchRow.rod_number
                              ? ` · Rod ${catchRow.rod_number}`
                              : ""}
                          </p>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <Field
                              label="Weight (lb)"
                              id={`end-weight-lb-${catchRow.id}`}
                            >
                              <Input
                                id={`end-weight-lb-${catchRow.id}`}
                                type="number"
                                min="0"
                                max="200"
                                value={draft.lb}
                                onChange={(event) =>
                                  setMissingWeightDrafts((current) => ({
                                    ...current,
                                    [key]: {
                                      ...draft,
                                      lb: event.target.value,
                                    },
                                  }))
                                }
                                placeholder="30"
                              />
                            </Field>
                            <Field
                              label="Weight (oz)"
                              id={`end-weight-oz-${catchRow.id}`}
                            >
                              <Input
                                id={`end-weight-oz-${catchRow.id}`}
                                type="number"
                                min="0"
                                max="15"
                                value={draft.oz}
                                onChange={(event) =>
                                  setMissingWeightDrafts((current) => ({
                                    ...current,
                                    [key]: {
                                      ...draft,
                                      oz: event.target.value,
                                    },
                                  }))
                                }
                                placeholder="8"
                              />
                            </Field>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {missingWeightError ? (
                    <p className="text-sm text-error" role="alert">
                      {missingWeightError}
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      isLoading={savingMissingWeights || ending}
                      onClick={saveMissingWeightsAndEnd}
                    >
                      Save weights & end session
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={savingMissingWeights || ending}
                      onClick={() => {
                        setReviewingMissingWeights(false);
                        setMissingWeightError("");
                      }}
                    >
                      ← Back
                    </Button>
                  </div>
                </div>
              ) : missingWeightCatches.length > 0 ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-primary">
                      {missingWeightCatches.length === 1
                        ? "1 catch is missing a weight"
                        : `${missingWeightCatches.length} catches are missing weights`}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-secondary">
                      Add them now so your personal bests and weight stats stay
                      accurate, or deliberately end the session without them.
                    </p>
                  </div>
                  {endError ? (
                    <p className="text-sm text-error">{endError}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={ending}
                      onClick={beginMissingWeightReview}
                    >
                      Add weights
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      isLoading={ending}
                      onClick={handleEndSession}
                    >
                      End session without weights
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={ending}
                      onClick={() => {
                        setShowEndConfirm(false);
                        setEndError("");
                      }}
                    >
                      Keep fishing
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-primary">
                    End this session now?
                  </p>
                  <p className="text-sm leading-6 text-secondary">
                    The current time will be recorded as the end time. CatchCompass
                    will build a session report from the session and catches, show it
                    here, and save a copy into the session notes.
                  </p>
                  {endError ? (
                    <p className="text-sm text-error">{endError}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      isLoading={ending}
                      onClick={handleEndSession}
                    >
                      End session
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={ending}
                      onClick={() => {
                        setShowEndConfirm(false);
                        setEndError("");
                      }}
                    >
                      Keep fishing
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-primary">
                Catches in this session
              </h2>
              {!session.end_at ? (
                <Button asChild size="md">
                  <Link to={`/catches/new?sessionId=${session.id}`}>
                    Log catch
                  </Link>
                </Button>
              ) : null}
            </div>
            {catches.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-secondary">
                No catches logged yet.
              </div>
            ) : (
              catches.map((catchRow: any) => (
                <Link
                  key={catchRow.id}
                  to={`/catches/${catchRow.id}`}
                  className="block rounded-lg border border-border bg-raised p-3 transition-colors hover:bg-inset"
                >
                  <p className="font-semibold text-primary">
                    {weightLabel(catchRow.weight_lbs, catchRow.weight_oz)} ·{" "}
                    {catchRow.species}
                  </p>
                  <p className="mt-1 text-sm text-secondary">
                    {formatDateTime(catchRow.caught_at)} ·{" "}
                    {catchRow.hookbait || "Hookbait not recorded"} ·{" "}
                    {catchRow.rig || "Rig not recorded"}
                  </p>
                  {weightLabel(catchRow.weight_lbs, catchRow.weight_oz) ===
                  "Weight not added" ? (
                    <p className="mt-1 text-xs font-medium text-accent">
                      Tap to add weight or complete details
                    </p>
                  ) : null}
                </Link>
              ))
            )}
          </section>

          {/* ── Destructive actions ── always at bottom of normal view */}
          <div className="border-t border-border pt-6">
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(true);
                  setDeleteError("");
                }}
                className="text-sm font-medium text-error hover:underline"
              >
                {session.end_at ? "Delete session" : "Cancel session"}
              </button>
            ) : (
              <div className="rounded-lg border border-error bg-error-weak p-4 space-y-3">
                <p className="text-sm font-semibold text-primary">
                  {session.end_at
                    ? "Delete this session?"
                    : "Cancel this session?"}
                </p>
                <p className="text-sm text-secondary leading-6">
                  {session.end_at
                    ? "This permanently removes this completed session and its catches/setup data. It will be removed from your stats and CarpIntel evidence. This cannot be undone."
                    : "This permanently removes the session and anything recorded during it. It will not count towards your stats or CarpIntel evidence. This cannot be undone."}
                </p>
                {deleteError ? (
                  <p className="text-sm text-error">{deleteError}</p>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteError("");
                    }}
                    disabled={deleting}
                  >
                    Keep session
                  </Button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="rounded-lg bg-error px-4 py-2 text-sm font-semibold text-error-fg disabled:opacity-50"
                  >
                    {deleting
                      ? "Removing…"
                      : session.end_at
                        ? "Delete session permanently"
                        : "Cancel and delete session"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {session ? (
        <CompanionOverlay
          context={{ type: "session", sessionId: session.id }}
          externalQuestion={companionQuestion}
        />
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-3">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 truncate text-lg font-semibold text-primary">
        {value}
      </p>
    </div>
  );
}
function MetricText({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-1 truncate font-medium text-primary">
        {value || "Not recorded"}
      </p>
    </div>
  );
}
function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      <p>{text}</p>
      {link ? (
        <Link
          to={link}
          className="mt-3 inline-block font-medium text-accent hover:underline"
        >
          Back to sessions
        </Link>
      ) : null}
    </div>
  );
}
